import React, { useState, useEffect } from 'react';
import { TryOnProvider, useTryOn } from '../contexts/TryOnContext';
import Step1Attract from '../components/Step1Attract';
import Step2Capture from '../components/Step2Capture';
import Step3UserInfo from '../components/Step3UserInfo';
import VideoGallery from '../components/VideoGallery';
import './HomePage.css';

/**
 * Internal component that renders the current step
 */
function HomePageContent() {
  const { currentStep } = useTryOn();
  const [displayStep, setDisplayStep] = useState(currentStep);
  // Has the new step been rendered yet? Drives the overlay fade-out.
  const [newStepRendered, setNewStepRendered] = useState(true);

  // Overlay is visible whenever currentStep is ahead of displayStep, OR the
  // new step has been swapped in but hasn't been given a paint yet. Derived
  // synchronously from state so it covers the very same render that clears
  // the previous step's data — no one-frame gap where stale state can flash.
  const transitioning = currentStep !== displayStep;
  const overlayVisible = transitioning || !newStepRendered;

  useEffect(() => {
    if (currentStep === displayStep) return;
    // Wait two frames for the opaque overlay to actually paint, then swap
    // the step behind it.
    const raf1 = requestAnimationFrame(() => {
      const raf2 = requestAnimationFrame(() => {
        setDisplayStep(currentStep);
        setNewStepRendered(false);
        // After the new step paints, mark it rendered so the overlay fades out.
        const raf3 = requestAnimationFrame(() => {
          requestAnimationFrame(() => setNewStepRendered(true));
        });
        return () => cancelAnimationFrame(raf3);
      });
      return () => cancelAnimationFrame(raf2);
    });
    return () => cancelAnimationFrame(raf1);
  }, [currentStep]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="homepage-container">
      {displayStep === 1 && <Step1Attract />}
      {displayStep === 2 && <Step2Capture />}
      {displayStep === 3 && <Step3UserInfo />}
      {displayStep === 4 && <VideoGallery />}
      <div className={`step-transition-overlay ${overlayVisible ? 'visible' : ''}`} />
    </div>
  );
}

/**
 * HomePage - Main try-on flow with 4 steps
 * Wrapped in TryOnProvider for state management
 */
function HomePage() {
  return (
    <TryOnProvider>
      <HomePageContent />
    </TryOnProvider>
  );
}

export default HomePage;
